import { axiosArisChatInstance } from "../AxiosConfiguration";

// Faith personalization profile lives in aris-ai (user_faith_profiles).
// core-lambda owns the entitlement + denomination toggle (see subscription.js
// getUserFaith / updateUserFaith). This file is ONLY the 7-step personalization
// profile + reminder reconciliation contract documented in
// docs/continuity_faith_implementation_plan.md (§2.2).
const FAITH_PROFILE_PATH = "/ARIS/v2/faith-profile";
const FAITH_SCHEMA_PATH = "/ARIS/v2/faith-profile/schema";
const SCHEMA_CACHE_KEY = "faith_schema_cache";

// The wizard is rendered from this server-driven schema (questions are a
// backend content change — never hardcode them). The endpoint sets an ETag and
// Cache-Control; we cache the schema + etag in localStorage and revalidate with
// If-None-Match. A 304 means "reuse your cached copy".
export const getFaithSchema = async (locale = "en") => {
  let cached = null;
  try {
    const raw = localStorage.getItem(SCHEMA_CACHE_KEY);
    if (raw) cached = JSON.parse(raw);
  } catch (_) {
    cached = null;
  }
  const haveValidCache = cached?.locale === locale && cached?.schema && cached?.etag;

  const response = await axiosArisChatInstance.get(FAITH_SCHEMA_PATH, {
    params: { locale },
    headers: haveValidCache ? { "If-None-Match": cached.etag } : {},
    // Accept 304 so axios doesn't throw; we serve the cache for it.
    validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
  });

  if (response.status === 304 && haveValidCache) {
    return cached.schema;
  }

  const schema = response?.data?.data?.schema ?? null;
  const etag = response?.headers?.etag;
  if (schema && etag) {
    try {
      localStorage.setItem(SCHEMA_CACHE_KEY, JSON.stringify({ etag, locale, schema }));
    } catch (_) {
      /* cache is best-effort */
    }
  }
  // If a 304 slipped through without a usable cache, fall back to cached schema.
  return schema || cached?.schema || null;
};

// GET — used to pre-fill / resume the wizard. Returns { success, data: { profile|null } }.
export const getFaithProfile = async () => {
  const response = await axiosArisChatInstance.get(FAITH_PROFILE_PATH);
  return response.data;
};

// PUT — always (re-)enables the profile and reconciles prayer/holiday reminders.
// Body is the FaithProfileInput (all fields optional, merge-upsert on the server).
// Returns { success, data: { profile, reconciliation } }.
export const updateFaithProfile = async (body) => {
  const response = await axiosArisChatInstance.put(FAITH_PROFILE_PATH, body);
  return response.data;
};

// DELETE — disables the profile: cancels all prayer/holiday reminders and
// archives habits, but keeps saved preferences for a later re-enable.
// Returns { success, data: { profile, reconciliation } }.
export const deleteFaithProfile = async () => {
  const response = await axiosArisChatInstance.delete(FAITH_PROFILE_PATH);
  return response.data;
};
